import * as React from 'react';
import { Grid } from '@mui/material';
import MNumber from './MNumber';

/**
 * Props for {@link MNumberRange}.
 */
interface MNumberRangeProps extends Omit<React.ComponentProps<typeof MNumber>, 'onChange' | 'defaultValue' | 'range'> {
    /** Base OData field name; combined with `_ge`/`_le` suffixes when reporting changes. */
    name: string;
    /**
     * Called once per bound whenever either the "from" or "to" value changes.
     *
     * @param value - The new value for the changed bound.
     * @param filterName - The OData field name with its `_ge`/`_le` suffix.
     */
    onChange: (value: number | string, filterName: string) => void;
    /** Initial `[from, to]` values. */
    defaultValue?: [number | string | undefined, number | string | undefined];
    /** Allowed `[min, max]` range applied to the "from" input. */
    rangeMin?: [number | undefined, number | undefined] | null;
    /** Allowed `[min, max]` range applied to the "to" input. */
    rangeMax?: [number | undefined, number | undefined] | null;
}

interface MNumberRangeState {
    from?: number | string;
    to?: number | string;
}

/**
 * A pair of {@link MNumber} fields ("from" / "to") reporting each bound
 * change independently via `onChange`, suffixed with the `_ge`/`_le` OData
 * operators.
 */
const MNumberRange: React.FC<MNumberRangeProps> = (props) => {
    const { name, onChange, defaultValue, rangeMin, rangeMax, ...newProps } = props;
    const [state, setState] = React.useState<MNumberRangeState>({ from: defaultValue?.[0], to: defaultValue?.[1] });

    React.useEffect(() => {
        setState((prev) => ({ ...prev, from: defaultValue?.[0], to: defaultValue?.[1] }));
    }, [defaultValue]);

    const handleChange = (value: number | string, label: 'from' | 'to', op: 'ge' | 'le') => {
        setState((prev) => ({ ...prev, [label]: value }));
        onChange(value, `${name}_${op}`);
    };

    return (
        <Grid container>
            <Grid size={6}>
                <MNumber {...newProps} defaultValue={state.from} range={rangeMin} onChange={(value) => handleChange(value, 'from', 'ge')} />
            </Grid>
            <Grid size={6}>
                <MNumber {...newProps} label="to" defaultValue={state.to} range={rangeMax} onChange={(value) => handleChange(value, 'to', 'le')} />
            </Grid>
        </Grid>
    );
};

export default React.memo(MNumberRange);
